import React, { useState, useRef, useEffect } from 'react';

interface TooltipProps {
  children: React.ReactNode;
  content: React.ReactNode;
  /** Position of tooltip relative to children */
  position?: 'top' | 'bottom' | 'left' | 'right';
  /** Whether to show the tooltip */
  disabled?: boolean;
  /** Custom class for tooltip container */
  className?: string;
}

/**
 * A simple tooltip component that shows on hover.
 * Automatically repositions if it would go off-screen.
 */
export function Tooltip({
  children,
  content,
  position = 'top',
  disabled = false,
  className = '',
}: TooltipProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isVisible && tooltipRef.current && containerRef.current) {
      const tooltipRect = tooltipRef.current.getBoundingClientRect();
      const containerRect = containerRef.current.getBoundingClientRect();

      // Check if tooltip goes off screen and adjust position
      if (position === 'top' && tooltipRect.top < 0) {
        setAdjustedPosition('bottom');
      } else if (position === 'bottom' && tooltipRect.bottom > window.innerHeight) {
        setAdjustedPosition('top');
      } else if (position === 'left' && tooltipRect.left < 0) {
        setAdjustedPosition('right');
      } else if (position === 'right' && tooltipRect.right > window.innerWidth) {
        setAdjustedPosition('left');
      } else {
        setAdjustedPosition(position);
      }

      // Also check horizontal centering for top/bottom positions
      if ((adjustedPosition === 'top' || adjustedPosition === 'bottom')) {
        const tooltipCenter = containerRect.left + containerRect.width / 2;
        const tooltipHalfWidth = tooltipRect.width / 2;

        // If tooltip would go off left edge, it will be constrained by CSS
        // If tooltip would go off right edge, it will be constrained by CSS
      }
    }
  }, [isVisible, position, adjustedPosition]);

  if (disabled) {
    return <>{children}</>;
  }

  const positionClasses = {
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-2',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-2',
    left: 'right-full top-1/2 -translate-y-1/2 mr-2',
    right: 'left-full top-1/2 -translate-y-1/2 ml-2',
  };

  const arrowClasses = {
    top: 'top-full left-1/2 -translate-x-1/2 border-t-gray-800 border-x-transparent border-b-transparent',
    bottom: 'bottom-full left-1/2 -translate-x-1/2 border-b-gray-800 border-x-transparent border-t-transparent',
    left: 'left-full top-1/2 -translate-y-1/2 border-l-gray-800 border-y-transparent border-r-transparent',
    right: 'right-full top-1/2 -translate-y-1/2 border-r-gray-800 border-y-transparent border-l-transparent',
  };

  return (
    <div
      ref={containerRef}
      className={`relative inline-block ${className}`}
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
      onTouchStart={() => setIsVisible(true)}
      onTouchEnd={() => setTimeout(() => setIsVisible(false), 2000)}
    >
      {children}
      {isVisible && (
        <div
          ref={tooltipRef}
          className={`absolute z-50 ${positionClasses[adjustedPosition]} pointer-events-none`}
          style={{ maxWidth: '250px', minWidth: '180px' }}
        >
          <div
            className="rounded-lg bg-gray-800 px-3 py-2 text-xs text-white shadow-lg"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            {content}
          </div>
          {/* Arrow */}
          <div
            className={`absolute border-4 ${arrowClasses[adjustedPosition]}`}
          />
        </div>
      )}
    </div>
  );
}

export default Tooltip;
